import React from 'react';
import { Tag, Button, Space, Tooltip } from 'antd';
import {
  EyeOutlined,
  CheckCircleOutlined,
  DollarCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  UserOutlined
} from '@ant-design/icons';

export const transactionColumns = [
  {
    title: 'N° Facture',
    dataIndex: 'numero',
    key: 'numero',
    width: 150,
    render: (text) => (
      <div className="font-mono font-semibold text-blue-600">
        {text || 'N/A'}
      </div>
    ),
  },
  {
    title: 'Désignation',
    dataIndex: 'designation',
    key: 'designation',
    width: 200,
    ellipsis: true,
    render: (text, record) => (
      <div>
        <div className="font-medium text-gray-800">{text || 'N/A'}</div>
        {record.chauffeur && (
          <div className="text-xs text-gray-500 mt-1">
            <UserOutlined className="mr-1" /> {record.chauffeur}
          </div>
        )}
      </div>
    ),
  },
  {
    title: 'Montant (FCFA)',
    dataIndex: 'montant',
    key: 'montant',
    width: 140,
    align: 'right',
    sorter: (a, b) => (a.montant || 0) - (b.montant || 0),
    render: (amount, record) => {
      if (record.statut === 'En attente') {
        return (
          <div className="text-right text-gray-400 italic">
            <div className="text-sm">Non validé</div>
          </div>
        );
      }
     
      return (
        <div className={`text-right ${record.statut === 'Payée' ? 'text-green-600' : 'text-blue-600'}`}>
          <div className="font-bold text-lg">{(amount || 0).toLocaleString('fr-FR')}</div>
          <div className="text-xs text-gray-500">FCFA</div>
        </div>
      );
    },
  },
  {
    title: 'Montant Versé',
    dataIndex: 'montantVerse',
    key: 'montantVerse',
    width: 140,
    align: 'right',
    render: (montantVerse, record) => {
      // CORRECTION: Afficher pour tous les statuts sauf En attente
      if (record.statut === 'En attente') return null;
     
      const verse = montantVerse || 0;
     
      return (
        <div className="text-right text-green-600">
          <div className="font-bold text-lg">{verse.toLocaleString('fr-FR')}</div>
          <div className="text-xs text-gray-500">FCFA</div>
          {verse > 0 && (
            <div className="text-xs text-green-500">Versé</div>
          )}
        </div>
      );
    },
  },
  {
    title: 'Montant Restant',
    dataIndex: 'montantRestant',
    key: 'montantRestant',
    width: 140,
    align: 'right',
    render: (montantRestant, record) => {
      // CORRECTION: Afficher pour les statuts Validée et Payée
      if (record.statut !== 'Validée' && record.statut !== 'Payée') return null;
     
      const restant = montantRestant || 0;
      const isComplete = restant <= 0;
     
      // CORRECTION: Pour les ventes Payées, le restant doit être 0
      if (record.statut === 'Payée' || isComplete) {
        return (
          <div className="text-right text-green-600">
            <div className="font-bold text-lg">0</div>
            <div className="text-xs text-gray-500">FCFA</div>
            <div className="text-xs text-green-500">Soldé</div>
          </div>
        );
      }
     
      return (
        <div className="text-right text-orange-600">
          <div className="font-bold text-lg">{restant.toLocaleString('fr-FR')}</div>
          <div className="text-xs text-gray-500">FCFA</div>
          <div className="text-xs text-orange-500">Formule: Total - Versé</div>
        </div>
      );
    },
  },
  {
    title: 'Quantité',
    dataIndex: 'quantite',
    key: 'quantite',
    width: 100,
    align: 'center',
    render: (qty) => (
      <div className="text-center">
        <div className="font-bold text-blue-600">{qty || 0}</div>
        <div className="text-xs text-gray-500">tonnes</div>
      </div>
    ),
  },
  {
    title: 'Statut',
    dataIndex: 'statut',
    key: 'statut',
    width: 130,
    filters: [
      { text: 'En attente', value: 'En attente' },
      { text: 'Validée', value: 'Validée' },
      { text: 'Payée', value: 'Payée' },
      { text: 'Annulée', value: 'Annulée' },
    ],
    onFilter: (value, record) => record.statut === value,
    render: (statut) => {
      let color = 'default';
      let icon = null;
      let text = statut;
     
      switch (statut) {
        case 'Validée':
          color = 'processing';
          icon = <CheckCircleOutlined />;
          text = 'Validée';
          break;
        case 'Payée':
          color = 'success';
          icon = <CheckCircleOutlined />;
          text = 'Payée';
          break;
        case 'En attente':
          color = 'warning';
          icon = <ClockCircleOutlined />;
          text = 'En attente';
          break;
        case 'Annulée':
          color = 'error';
          icon = <CloseCircleOutlined />;
          text = 'Annulée';
          break;
        default:
          color = 'default';
          icon = <ExclamationCircleOutlined />;
      }
     
      return (
        <Tag color={color} icon={icon} className="font-medium px-3 py-1">
          {text}
        </Tag>
      );
    },
  },
  {
    title: 'Date',
    dataIndex: 'dateFormatted',
    key: 'date',
    width: 150,
    sorter: (a, b) => new Date(a.date) - new Date(b.date),
    render: (date, record) => (
      <div className="text-sm">
        <div className="font-medium">{date ? date.split(' ')[0] : 'N/A'}</div>
        <div className="text-xs text-gray-500">{date ? date.split(' ')[1] : ''}</div>
      </div>
    ),
  },
  {
    title: 'Actions',
    key: 'actions',
    width: 180,
    fixed: 'right',
    render: (_, record, helpers) => {
      // CORRECTION: Utiliser les helpers comme des fonctions
      const calculateResult = helpers?.calculateMontantRestant ? helpers.calculateMontantRestant() : { montantRestant: 0 };
      const montantRestant = calculateResult.montantRestant || 0;
      const hasVersements = helpers?.verifierSiMontantVerseExiste ? helpers.verifierSiMontantVerseExiste() : false;
     
      return (
        <Space size="small">
          <Tooltip title="Voir détails">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => helpers?.viewVenteDetail && helpers.viewVenteDetail(record.id)}
              className="text-blue-500 hover:text-blue-700"
              size="small"
            />
          </Tooltip>
         
          {/* Bouton Validation Paiement - s'affiche si des versements ont été faits */}
          {record.statut === 'Validée' && montantRestant > 0 && hasVersements && (
            <Tooltip title="Valider le paiement complet">
              <Button
                type="primary"
                danger
                icon={<CheckCircleOutlined />}
                onClick={() => helpers?.handleValidationPaiement && helpers.handleValidationPaiement(record)}
                className="hover:bg-red-600"
                size="small"
              >
                Valider
              </Button>
            </Tooltip>
          )}
         
          {/* Bouton Montant Versé pour les ventes Validées avec montant restant */}
          {record.statut === 'Validée' && montantRestant > 0 && !hasVersements && (
            <Tooltip title="Enregistrer montant versé">
              <Button
                type="primary"
                icon={<DollarCircleOutlined />}
                onClick={() => helpers?.handleMontantVerse && helpers.handleMontantVerse(record)}
                className="bg-green-500 hover:bg-green-600 border-green-500"
                size="small"
              >
                Versement
              </Button>
            </Tooltip>
          )}
         
          {/* Message pour ventes Payées */}
          {record.statut === 'Payée' && (
            <Tooltip title="Paiement complet">
              <Tag color="success" className="mr-0">
                <CheckCircleOutlined /> Payé
              </Tag>
            </Tooltip>
          )}
        </Space>
      );
    },
  },
];